import '@tempus/core-design-foundation/foundation.css';
import { TcdConfigProvider } from '@tempus/core-design-theme-antd';
import { ConfigProvider } from 'antd';
import React, { createContext, useContext, useMemo } from 'react';

import { useLocale } from '~/i18n';

import { getAntdLocale } from './antdLocales';
import { getAntdThemeConfig, type AntdAppearance, type AntdContrast, type AntdDirection } from './antdThemes';

type AntdThemeContextValue = {
  appearance: AntdAppearance;
  contrast: AntdContrast;
  direction: AntdDirection;
};

const AntdThemeContext = createContext<AntdThemeContextValue>({
  appearance: 'lightCarbon',
  contrast: 'default',
  direction: 'ltr',
});

export const useAntdAppearance = (): AntdAppearance => useContext(AntdThemeContext).appearance;

export const useAntdContrast = (): AntdContrast => useContext(AntdThemeContext).contrast;

export const useAntdDirection = (): AntdDirection => useContext(AntdThemeContext).direction;

export type AntdThemeProviderProps = {
  appearance?: AntdAppearance;
  contrast?: AntdContrast;
  direction?: AntdDirection;
  children: React.ReactNode;
};

const isJest = process.env.NODE_ENV === 'test';

const AntdThemeProvider: React.FC<AntdThemeProviderProps> = ({
  appearance = 'lightCarbon',
  contrast = 'default',
  direction = 'ltr',
  children,
}) => {
  const localeCode = useLocale();
  const themeConfig = useMemo(() => {
    const config = getAntdThemeConfig(appearance, contrast);
    if (!isJest) {
      return config;
    }
    // Skip CSS-in-JS and motion. Set zeroRuntime on the theme object.
    // TcdConfigProvider's zeroRuntime prop also merges zeroRuntimeThemeOptions
    // (hashed: false, cssVar key "tcd"), which drops the therapies-antd prefix.
    // token.motion false turns CSSMotion off. jsdom never fires transitionend, and
    // the button wave otherwise waits 5000ms — Jest's default test timeout.
    return {
      ...config,
      zeroRuntime: true,
      token: { ...config?.token, motion: false },
    };
  }, [appearance, contrast]);
  const locale = useMemo(() => getAntdLocale(localeCode), [localeCode]);
  const contextValue = useMemo(() => ({ appearance, contrast, direction }), [appearance, contrast, direction]);
  const providerProps = {
    direction,
    locale,
    theme: themeConfig,
    virtual: isJest ? false : undefined,
    ...(isJest ? { wave: { disabled: true } } : {}),
  };

  return (
    <AntdThemeContext.Provider value={contextValue}>
      {appearance === 'disabled' ? (
        <ConfigProvider {...providerProps}>{children}</ConfigProvider>
      ) : (
        <TcdConfigProvider {...providerProps}>{children}</TcdConfigProvider>
      )}
    </AntdThemeContext.Provider>
  );
};

export default AntdThemeProvider;
